import { ReactNode, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../context/BrandContext";
import { parseFleetClaims, canAdminPlatform } from "../lib/fleetRoles";
import { OrgSwitcher } from "./OrgSwitcher";
import { TrialBanner, TierExpiredBlock } from "./TrialBanner";
import { colors, fontSizes, radii, spacing, transitions } from "../styles/theme";
import { platformApi, type AuthPendingJob } from "../api/platformApi";
import { useGravatarUrl } from "../hooks/useGravatar";
import { CommandPalette } from "./CommandPalette";
import { KeyboardShortcutsSheet } from "./KeyboardShortcutsSheet";
import { ThemeToggle } from "./ThemeToggle";
import { ThemeHydrator } from "../context/ThemeContext"; // #2325
import { SupportChatMount } from "./support/SupportChatMount"; // #3105

// ── Height constants (exported for pages that need viewport offset) ────────────
export const TOPBAR_PRIMARY_H   = 52;
export const TOPBAR_SECONDARY_H = 44;
export const TOPBAR_TOTAL_H     = TOPBAR_PRIMARY_H + TOPBAR_SECONDARY_H; // 96

// ── Route group detection ──────────────────────────────────────────────────────
function isFleetRoute(p: string) {
  return p.startsWith("/fleet") ||
    p.startsWith("/servers") ||
    p.startsWith("/integrations") || // Integrations is fleet-related (#int-nav-move)
    p === "/hives" || p.startsWith("/hives/") ||
    p === "/all-hives" ||
    p.startsWith("/admin/cost-dashboard") ||  // #2229
    p.startsWith("/admin/deploy-status") ||    // #2231
    p === "/audit" || p.startsWith("/audit/") ||
    p.startsWith("/admin/support");              // #2444 support inbox
}

// Routes accessible while tier_expired (upgrade + billing only)
const TIER_EXPIRED_ALLOWED = ["/upgrade", "/upgrade-success", "/billing", "/account/billing", "/account"];
function isTierExpiredAllowed(p: string) {
  return TIER_EXPIRED_ALLOWED.some(a => p === a || p.startsWith(a + "/"));
}

// ── Secondary nav definitions ──────────────────────────────────────────────────
const FLEET_NAV: ReadonlyArray<{ label: string; path: string; platform?: true }> = [
  { label: "Hives",        path: "/hives" },
  { label: "All Hives",    path: "/all-hives", platform: true }, // admin-only: #1447 cross-tenant admin grid
  { label: "Board",        path: "/fleet/board" },
  { label: "Integrations", path: "/integrations" },
  // Servers hidden from nav (BYO-server mgmt irrelevant to users); route + page
  // kept for deep-link/admin (App.tsx) + isFleetRoute still matches it. (#servers-hide)
  { label: "Cost",         path: "/admin/cost-dashboard",  platform: true }, // admin-only: #2229
  { label: "Deploys",      path: "/admin/deploy-status",   platform: true }, // admin-only: #2231
  { label: "Audit Log",    path: "/audit", platform: true }, // admin-only: /audit 403s for non-admins (#1636)
  { label: "Support",      path: "/admin/support", platform: true }, // admin-only: support@2hives.ai inbox (#2444)
];

// SSH Key is intentionally available to ALL tiers (#2917) — SSH access is
// core to the product, every user gets a box reachable over SSH. Do not
// add a tier filter here.
const ACCOUNT_NAV: ReadonlyArray<{ label: string; path: string }> = [
  { label: "Profile",      path: "/profile" },
  { label: "SSH Key",      path: "/profile/ssh-key" },
  { label: "Account",      path: "/account" },
  { label: "Billing",      path: "/account/billing" }, // #2442 — customer-facing billing page
  { label: "Usage",        path: "/usage" },
  { label: "Upgrade",      path: "/upgrade" },
  { label: "Support",      path: "/support" },  // #2444 customer ticket history
];

// ── Balance badge ──────────────────────────────────────────────────────────────
function BalanceBadge() {
  const { profile } = useAuth();
  const navigate    = useNavigate();

  if (!profile) return null;
  const tier    = profile.tier;
  const balance = profile.balance_cents;
  const used    = profile.tokens_used_today ?? 0;
  const cap     = profile.token_budget_daily;
  const dailyPct = cap && cap > 0 ? (used / cap) * 100 : 0;
  const showBadge = tier != null || (cap != null && used > 0);
  if (!showBadge) return null;

  const isRed    = (balance != null && balance < 0) || dailyPct > 100;
  const isYellow = !isRed && ((balance != null && balance < 1_000) || dailyPct > 80);
  // #3115 / #3153 — the topbar balance badge used `colors.green/.warning/
  // .error` (dark hexes) for both the dot AND the text. In light mode the
  // pale-green "92K" text on the bg-sunken header was illegible. We split:
  //   - `dotColor`   stays the saturated hue (acceptable as a 6px dot)
  //   - `textColor`  uses the *-text variant which carries the light-mode-
  //                  tuned darker shade (dark green light / bright dark).
  //   - bg/border    use the *-bg token + the dot hue at a low alpha
  //                  via color-mix() so the wash works in both modes.
  const dotColor  = isRed ? "var(--brand-status-error)"
                  : isYellow ? "var(--brand-status-warning)"
                             : "var(--brand-status-success)";
  const textColor = isRed ? "var(--brand-status-error-text)"
                  : isYellow ? "var(--brand-status-warning-text)"
                             : "var(--brand-status-success-text)";
  const bgWash    = isRed ? "var(--brand-status-error-bg)"
                  : isYellow ? "var(--brand-status-warning-bg)"
                             : "var(--brand-status-success-bg)";

  return (
    <button
      onClick={() => navigate("/account/billing")}
      style={{
        display: "flex", alignItems: "center", gap: 5,
        padding: "4px 9px", flexShrink: 0,
        background: bgWash,
        border: `1px solid color-mix(in srgb, ${dotColor} 35%, transparent)`,
        borderRadius: radii.pill, cursor: "pointer", fontFamily: "inherit",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
      {balance != null && (
        <span className="fleet-balance-text" style={{ fontSize: "0.65rem", fontWeight: 700, color: textColor }}>
          {balance < 0 ? "-" : ""}${Math.abs(balance / 100).toFixed(0)}
        </span>
      )}
      {cap != null && cap > 0 && (
        <span className="fleet-balance-text" style={{ fontSize: "0.65rem", color: textColor }}>{dailyPct.toFixed(0)}%</span>
      )}
    </button>
  );
}

// ── Avatar dropdown ────────────────────────────────────────────────────────────
function AvatarDropdown({ email, avatarUrl, onSignOut }: { email: string; avatarUrl?: string | null; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const gravatarUrl = useGravatarUrl(email, 44);
  const [imgFailed, setImgFailed] = useState(false);
  const photoSrc = avatarUrl ?? (!imgFailed ? gravatarUrl : null);
  // Multi-account workspace switcher (#1649)
  const { accounts, activeSub, switchAccount, signOutAccount } = useAuth();
  const hasMultiple = accounts.length > 1;

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const initial = email[0]?.toUpperCase() ?? "?";

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="avatar-dropdown"
        style={{
          display: "flex", alignItems: "center", gap: 6,
          background: open ? "rgba(247,220,111,0.08)" : "transparent",
          border: `1px solid ${open ? colors.goldSecondary + "55" : "transparent"}`,
          borderRadius: radii.sm, padding: "4px 8px", minHeight: 36,
          cursor: "pointer", fontFamily: "inherit", transition: transitions.fast,
        }}
      >
        {photoSrc ? (
          <img
            src={photoSrc}
            alt=""
            width={22}
            height={22}
            style={{ borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
            onError={() => setImgFailed(true)}
          />
        ) : (
          <span style={{
            width: 22, height: 22, borderRadius: "50%",
            background: `linear-gradient(135deg, ${colors.goldPrimary}, ${colors.goldSecondary})`,
            color: "var(--brand-text-inverse)", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "0.65rem", fontWeight: 700, flexShrink: 0,
          }}>{initial}</span>
        )}
        {/* #3115 / #3153 — was `colors.textMuted` (dark hex constant) on
            the light-mode topbar bg-sunken ~2.3:1, AA fail. Switched to
            `--brand-text-secondary` which carries 7.77:1 light / 7.78:1 dark. */}
        <span className="fleet-avatar-email" style={{
          fontSize: "0.7rem", color: "var(--brand-text-secondary)",
          maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{email}</span>
        <span style={{ fontSize: "0.55rem", color: "var(--brand-text-secondary)", opacity: 0.7 }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 200,
          background: "var(--brand-bg-elevated)", border: `1px solid ${colors.borderDefault}`,
          borderRadius: radii.md, padding: "4px", minWidth: 220,
          boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
        }}>
          {/* ── Workspace switcher (#1649) ── */}
          <div style={{
            fontSize: "0.55rem", color: colors.textMuted, textTransform: "uppercase",
            letterSpacing: "0.08em", padding: "5px 12px 3px", opacity: 0.65,
          }}>
            {hasMultiple ? "Workspaces" : "Workspace"}
          </div>
          {accounts.map((a) => {
            const isActive = a.sub === activeSub;
            return (
              <div
                key={a.sub}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  borderRadius: radii.sm,
                  background: isActive ? "rgba(247,220,111,0.10)" : "transparent",
                }}
              >
                <button
                  data-testid="workspace-switch"
                  onClick={() => { if (!isActive) switchAccount(a.sub); setOpen(false); }}
                  title={a.email}
                  style={{
                    flex: 1, display: "flex", alignItems: "center", gap: 8, minWidth: 0,
                    textAlign: "left", background: "transparent", border: "none",
                    borderRadius: radii.sm, padding: "7px 10px", cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  <span style={{
                    width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
                    background: `linear-gradient(135deg, ${colors.goldPrimary}, ${colors.goldSecondary})`,
                    color: "var(--brand-text-inverse)", display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "0.55rem", fontWeight: 700,
                  }}>{a.email[0]?.toUpperCase() ?? "?"}</span>
                  <span style={{
                    flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    fontSize: fontSizes.xs, color: isActive ? colors.goldPrimary : colors.textMuted,
                    fontWeight: isActive ? 700 : 400,
                  }}>{a.label}</span>
                  {isActive && <span style={{ fontSize: "0.7rem", color: colors.goldPrimary, flexShrink: 0 }}>✓</span>}
                </button>
                <button
                  onClick={() => {
                    signOutAccount(a.sub);
                    if (accounts.length <= 1) { setOpen(false); navigate("/login"); }
                  }}
                  title={`Sign out ${a.email}`}
                  style={{
                    flexShrink: 0, background: "transparent", border: "none", color: colors.textMuted,
                    padding: "7px 10px", fontSize: "0.7rem", cursor: "pointer", fontFamily: "inherit", opacity: 0.6,
                  }}
                  onMouseEnter={(e) => { (e.target as HTMLElement).style.opacity = "1"; }}
                  onMouseLeave={(e) => { (e.target as HTMLElement).style.opacity = "0.6"; }}
                >✕</button>
              </div>
            );
          })}
          <button
            data-testid="add-account"
            onClick={() => { navigate("/login"); setOpen(false); }}
            style={{
              display: "block", width: "100%", textAlign: "left",
              background: "transparent", border: "none",
              borderRadius: radii.sm, color: colors.textSubtle,
              padding: "7px 12px", fontSize: fontSizes.xs,
              cursor: "pointer", fontFamily: "inherit", transition: transitions.fast,
            }}
            onMouseEnter={(e) => { (e.target as HTMLElement).style.background = "rgba(247,220,111,0.08)"; }}
            onMouseLeave={(e) => { (e.target as HTMLElement).style.background = "transparent"; }}
          >
            + Add account
          </button>
          <div style={{ height: 1, background: colors.borderDefault, margin: "4px 0" }} />

          {([ { label: "Profile", path: "/profile" }, { label: "Danger zone", path: "/danger-zone" } ] as const).map(({ label, path }) => (
            <button
              key={path}
              onClick={() => { navigate(path); setOpen(false); }}
              style={{
                display: "block", width: "100%", textAlign: "left",
                background: "transparent", border: "none",
                borderRadius: radii.sm, color: colors.textMuted,
                padding: "7px 12px", fontSize: fontSizes.xs,
                cursor: "pointer", fontFamily: "inherit", transition: transitions.fast,
              }}
              onMouseEnter={(e) => { (e.target as HTMLElement).style.background = "rgba(247,220,111,0.08)"; }}
              onMouseLeave={(e) => { (e.target as HTMLElement).style.background = "transparent"; }}
            >
              {label}
            </button>
          ))}
          <div style={{ height: 1, background: colors.borderDefault, margin: "4px 0" }} />
          <button
            onClick={() => { onSignOut(); setOpen(false); }}
            style={{
              display: "block", width: "100%", textAlign: "left",
              background: "transparent", border: "none",
              borderRadius: radii.sm, color: colors.error,
              padding: "7px 12px", fontSize: fontSizes.xs,
              cursor: "pointer", fontFamily: "inherit", transition: transitions.fast,
            }}
            onMouseEnter={(e) => { (e.target as HTMLElement).style.background = "rgba(201,64,64,0.08)"; }}
            onMouseLeave={(e) => { (e.target as HTMLElement).style.background = "transparent"; }}
          >
            {hasMultiple ? "Sign out of all workspaces" : "Sign out"}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Secondary tab button ───────────────────────────────────────────────────────
function SecondaryTab({ label, path, active, onClick }: {
  label: string; path: string; active: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      data-path={path}
      data-active={active ? "true" : undefined}
      style={{
        padding: "5px 12px", minHeight: 32, flexShrink: 0,
        background: "transparent", border: "none",
        // #3115 / #3153 — pale gold accent text on light-mode bg-sunken
        // was ~1.4:1 (severe AA fail). The gold underline stays (it's a
        // 2px stroke, not body text — the saturation reads even at low
        // contrast). The TEXT colour switches to brand-text-primary which
        // is dark in light + pale in dark.
        borderBottom: active ? "2px solid var(--brand-accent-secondary)" : "2px solid transparent",
        borderRadius: 0,
        color: active ? "var(--brand-text-primary)" : "var(--brand-text-secondary)",
        fontSize: fontSizes.xs, fontWeight: active ? 700 : 400,
        cursor: "pointer", fontFamily: "inherit",
        transition: transitions.fast, whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );
}

// ── Auth-required modal ────────────────────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  background: "var(--brand-bg-sunken)", border: `1px solid ${colors.borderDefault}`,
  borderRadius: radii.sm, color: colors.textBody,
  padding: "0.55rem 0.75rem", fontSize: fontSizes.sm,
  width: "100%", boxSizing: "border-box",
  fontFamily: "inherit", outline: "none",
};

function AuthRequiredModal({ job, onDismiss }: { job: AuthPendingJob; onDismiss: () => void }) {
  const [tok, setTok]   = useState("");
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState<string | null>(null);

  async function submit() {
    if (!tok.trim()) return;
    setBusy(true); setErr(null);
    try {
      await platformApi.submitAuthToken(job.id, job.user, tok.trim());
      onDismiss();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Submission failed");
    } finally { setBusy(false); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--brand-bg-elevated)", border: `1px solid ${colors.goldSecondary}`, borderRadius: radii.lg, padding: "1.75rem", width: 520, maxWidth: "95vw", display: "flex", flexDirection: "column", gap: spacing.md }}>
        <div style={{ fontWeight: 700, color: colors.goldPrimary, fontSize: fontSizes.base }}>Claude authentication required</div>
        <p style={{ margin: 0, color: colors.textMuted, fontSize: fontSizes.sm }}>
          Session <code style={{ color: colors.textSubtle, background: "var(--brand-bg-sunken)", padding: "1px 5px", borderRadius: 3 }}>{job.user}</code> needs Claude authentication. Open the URL, sign in, then paste the token here.
        </p>
        <div style={{ background: "var(--brand-bg-sunken)", padding: "0.65rem 0.75rem", borderRadius: radii.sm, fontSize: fontSizes.xs, color: colors.textSubtle, wordBreak: "break-all", border: `1px solid ${colors.borderDefault}` }}>{job.url}</div>
        <button onClick={() => window.open(job.url, "_blank", "noopener,noreferrer")} style={{ background: `linear-gradient(135deg, ${colors.goldPrimary}, ${colors.goldSecondary})`, color: "var(--brand-text-inverse)", border: "none", borderRadius: radii.sm, padding: "0.65rem 1.25rem", fontWeight: 700, fontSize: fontSizes.sm, cursor: "pointer", fontFamily: "inherit" }}>Open URL in browser ↗</button>
        <div>
          <label style={{ fontSize: fontSizes.xs, color: colors.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 4 }}>Paste auth token</label>
          <textarea value={tok} onChange={(e) => setTok(e.target.value)} rows={3} placeholder="Paste the authentication token from the browser…" style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5, fontFamily: "monospace" }} />
        </div>
        {err && <div style={{ color: colors.error, fontSize: fontSizes.sm }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onDismiss} style={{ background: "transparent", border: `1px solid ${colors.borderDefault}`, borderRadius: radii.sm, color: colors.textMuted, padding: "0.55rem 1rem", fontSize: fontSizes.sm, cursor: "pointer", fontFamily: "inherit" }}>Dismiss</button>
          <button onClick={submit} disabled={!tok.trim() || busy} style={{ background: tok.trim() && !busy ? `linear-gradient(135deg, ${colors.goldPrimary}, ${colors.goldSecondary})` : "rgba(247,220,111,0.15)", color: tok.trim() && !busy ? "var(--brand-text-inverse)" : colors.textMuted, border: "none", borderRadius: radii.sm, padding: "0.55rem 1.25rem", fontWeight: 700, fontSize: fontSizes.sm, cursor: tok.trim() && !busy ? "pointer" : "default", fontFamily: "inherit" }}>
            {busy ? "Submitting…" : "Submit token"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── FleetLayout ────────────────────────────────────────────────────────────────
export function FleetLayout({ children }: { children: ReactNode }) {
  const { token, logout, profile } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const claims    = parseFleetClaims(token);
  const isAdmin   = canAdminPlatform(profile?.fleet_role ? { ...claims, role: profile.fleet_role } : claims);
  const brand     = useBrand();

  const email = claims.email ?? (profile as { email?: string } | null)?.email ?? "";

  const [authJob,       setAuthJob]       = useState<AuthPendingJob | null>(null);
  const [mobileOpen,    setMobileOpen]    = useState(false);
  // #2323 command palette
  const [paletteOpen,   setPaletteOpen]   = useState(false);
  // #2324 keyboard shortcuts cheat sheet
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const activeGroup: "fleet" | "account" = isFleetRoute(location.pathname) ? "fleet" : "account";

  // Auth-pending poll
  useEffect(() => {
    let active = true;
    async function poll() {
      try {
        const jobs = await platformApi.listAuthJobs();
        if (active && jobs.length > 0 && !authJob) setAuthJob(jobs[0]);
      } catch { /* non-critical */ }
    }
    poll();
    const id = setInterval(poll, 10_000);
    return () => { active = false; clearInterval(id); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close drawer on navigation
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // #2323 Cmd+K → command palette
  // #2324 ? → shortcuts sheet; Cmd+N → new card (navigate to board)
  const openPaletteRef = useRef(paletteOpen);
  openPaletteRef.current = paletteOpen;

  const locationRef = useRef(location);
  locationRef.current = location;

  const handleGlobalKey = useCallback((e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement)?.tagName ?? "";
    const inInput = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" ||
      (e.target as HTMLElement)?.isContentEditable;

    const mod = e.metaKey || e.ctrlKey;

    // Cmd+K — toggle palette
    if (mod && e.key === "k") {
      e.preventDefault();
      setPaletteOpen((o) => !o);
      return;
    }

    // Cmd+N — new card: if already on board the board's own listener handles it;
    // from other pages, navigate to the board with ?new=1 (board reads this flag).
    if (mod && e.key === "n") {
      const onBoard = locationRef.current.pathname.startsWith("/fleet/board");
      if (!onBoard) {
        e.preventDefault();
        navigate("/fleet/board?new=1");
      }
      // When already on board let the board's own keydown handler fire
      return;
    }

    // ? — shortcuts sheet (only when not in an input)
    if (!inInput && e.key === "?") {
      e.preventDefault();
      setShortcutsOpen((o) => !o);
      return;
    }
  }, [navigate]);

  useEffect(() => {
    document.addEventListener("keydown", handleGlobalKey);
    return () => document.removeEventListener("keydown", handleGlobalKey);
  }, [handleGlobalKey]);

  function doSignOut() { logout(); navigate("/login"); }

  const fleetItems   = FLEET_NAV.filter((x) => !x.platform || isAdmin);
  const accountItems = Array.from(ACCOUNT_NAV);
  const secondaryItems = activeGroup === "fleet" ? fleetItems : accountItems;

  // #2805 — prefix-only `startsWith` matched siblings (Profile ⊃ Profile/SSH
  // Key, Account ⊃ Account/Billing) and lit two tabs at once. Resolve the
  // active tab by picking the longest path that matches the current
  // pathname — i.e. a sibling with a deeper, also-matching path wins over
  // its shallower parent. Both exact match and prefix match are still
  // accepted for the winner, so /profile lights only "Profile" and
  // /profile/ssh-key lights only "SSH Key".
  const navMatchLength = useCallback((path: string): number => {
    const p = location.pathname;
    if (path === "/fleet/board" || path === "/fleet") {
      return p === path ? path.length : -1;
    }
    if (p === path || p.startsWith(path + "/")) return path.length;
    return -1;
  }, [location.pathname]);

  const bestMatchLen = Math.max(
    -1,
    ...secondaryItems.map(({ path }) => navMatchLength(path)),
  );

  function navActive(path: string): boolean {
    const len = navMatchLength(path);
    return len >= 0 && len === bestMatchLen;
  }

  // #3115 / #3153 — same fix as SecondaryTab: gold-on-cream tab text
  // was an AA fail in light mode. Keep the gold underline as a stroke
  // signal, but use brand text tokens for the body text.
  const primaryTabStyle = (active: boolean): React.CSSProperties => ({
    padding: "5px 14px", minHeight: 36,
    background: "transparent", border: "none",
    borderBottom: active ? "2px solid var(--brand-accent-secondary)" : "2px solid transparent",
    borderRadius: 0,
    color: active ? "var(--brand-text-primary)" : "var(--brand-text-secondary)",
    fontSize: fontSizes.sm, fontWeight: active ? 700 : 400,
    cursor: "pointer", fontFamily: "inherit",
    transition: transitions.fast, whiteSpace: "nowrap", flexShrink: 0,
  });

  const drawerRowStyle = (active: boolean): React.CSSProperties => ({
    display: "block", width: "100%", textAlign: "left",
    background: active ? "var(--brand-bg-overlay)" : "transparent",
    border: "none",
    borderLeft: active ? "3px solid var(--brand-accent-secondary)" : "3px solid transparent",
    color: active ? "var(--brand-text-primary)" : "var(--brand-text-secondary)",
    padding: "9px 16px", fontSize: fontSizes.sm,
    cursor: "pointer", fontFamily: "inherit",
  });

  return (
    <>
      <style>{`
        .fleet-primary-tabs { display: flex; }
        .fleet-hamburger    { display: none !important; }
        .fleet-secondary    { display: flex !important; }
        .fleet-secondary::-webkit-scrollbar { display: none; }
        /* #2235 — the right-side header cluster (OrgSwitcher + BalanceBadge +
           AvatarDropdown email) totals ~310px and forces 375px viewports to
           overflow by ~110px. Hide the workspace pill + email text under 480px
           — both stay reachable via the avatar dropdown / hamburger drawer. */
        .fleet-org-switcher { display: flex; }
        .fleet-avatar-email { display: inline; }
        .fleet-balance-text { display: inline; }
        @media (max-width: 640px) {
          .fleet-primary-tabs { display: none !important; }
          .fleet-hamburger    { display: flex !important; }
          .fleet-secondary    { display: none !important; }
        }
        @media (max-width: 480px) {
          .fleet-org-switcher { display: none !important; }
          .fleet-avatar-email { display: none !important; }
          .fleet-balance-text { display: none !important; }
          .fleet-cmdK-btn     { display: none !important; }
        }
      `}</style>

      <div style={{
        display: "flex", flexDirection: "column", minHeight: "100dvh",
        // #3115 / #3153 — `colors.bgDeep` is a dark-hex constant frozen to
        // dark mode. In light mode this kept the page background dark green
        // while the inner cards were white, producing an unintended dual-
        // tone. The CSS-var token switches per mode (dark / light bg-default).
        backgroundColor: "var(--brand-bg-default)",
        color: "var(--brand-text-primary)",
        fontFamily: "'Inter', system-ui, sans-serif",
        // #2235 — prevent stray children (e.g. the kanban grid's min-width:1100
        // inside its own overflow-x:auto wrapper) from blowing out the whole
        // page width on narrow viewports. Page-level scroll-x is never wanted
        // here; per-pane scroll-x is.
        overflowX: "hidden",
      }}>

        {/* ── Sticky two-tier header ── */}
        <header style={{ position: "sticky", top: 0, zIndex: 100, backgroundColor: "var(--brand-bg-sunken)" }}>

          {/* Primary row */}
          <div style={{ height: TOPBAR_PRIMARY_H, display: "flex", alignItems: "stretch", padding: "0 14px", gap: 6, borderBottom: "1px solid rgba(42,74,58,0.5)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginRight: 4 }}>
              <img src={brand.logo_url} alt={brand.name} style={{ width: 26, height: 26, flexShrink: 0, cursor: "pointer" }} onClick={() => navigate("/fleet/board")} />
              <span style={{ fontWeight: 700, fontSize: fontSizes.sm, color: brand.accent_color, letterSpacing: "0.04em", flexShrink: 0 }}>{brand.name}</span>
            </div>

            {/* Primary tabs (desktop) */}
            <div className="fleet-primary-tabs" data-testid="primary-nav" style={{ alignItems: "stretch", gap: 0 }}>
              <button
                data-testid="primary-tab-fleet"
                data-active={activeGroup === "fleet" ? "true" : "false"}
                style={primaryTabStyle(activeGroup === "fleet")}
                onClick={() => { if (activeGroup !== "fleet") navigate("/fleet/board"); }}
              >
                Fleet
              </button>
              <button
                data-testid="primary-tab-account"
                data-active={activeGroup === "account" ? "true" : "false"}
                style={primaryTabStyle(activeGroup === "account")}
                onClick={() => { if (activeGroup !== "account") navigate("/profile"); }}
              >
                Account
              </button>
            </div>

            {/* Hamburger (mobile) */}
            <button
              className="fleet-hamburger"
              onClick={() => setMobileOpen((o) => !o)}
              style={{ alignItems: "center", justifyContent: "center", background: "transparent", border: "none", color: colors.textMuted, cursor: "pointer", padding: "6px 8px", borderRadius: radii.sm, fontSize: "1.2rem", fontFamily: "inherit" }}
              aria-label="Open navigation"
            >
              ☰
            </button>

            <div style={{ flex: 1 }} />
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {/* #2323 — Cmd+K palette trigger */}
              {/* #3115 / #3153 — Cmd+K trigger: muted text + subtle border
                  needed light-mode-aware tokens, otherwise the ⌘K glyph
                  was illegible on the bg-sunken header in light mode. */}
              <button
                onClick={() => setPaletteOpen((o) => !o)}
                title="Command palette (⌘K)"
                aria-label="Open command palette"
                data-testid="cmd-palette-btn"
                className="fleet-cmdK-btn"
                style={{
                  display: "flex", alignItems: "center", gap: 4,
                  background: "transparent",
                  border: "1px solid var(--brand-border-subtle)",
                  borderRadius: radii.sm, padding: "4px 8px",
                  color: "var(--brand-text-secondary)", cursor: "pointer",
                  fontFamily: "inherit", fontSize: fontSizes.xs,
                  transition: transitions.fast,
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--brand-border-strong)"; (e.currentTarget as HTMLButtonElement).style.color = "var(--brand-text-primary)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--brand-border-subtle)"; (e.currentTarget as HTMLButtonElement).style.color = "var(--brand-text-secondary)"; }}
              >
                <span>⌘K</span>
              </button>
              {/* #3105 — support drawer trigger + widget (login-gated). The
                  mount renders both the inline pill button (here) and the
                  fixed-position 420px right-side drawer when the user opens
                  it. Sits next to Cmd+K so support is one tab away from the
                  command palette. */}
              <SupportChatMount embed="drawer" referrerOrigin="admin_drawer" />
              {/* #2325 — theme toggle */}
              <ThemeToggle />
              <span className="fleet-org-switcher" style={{ alignItems: "center" }}>
                <OrgSwitcher />
              </span>
              <BalanceBadge />
              {email && <AvatarDropdown email={email} avatarUrl={profile?.avatar_url} onSignOut={doSignOut} />}
            </div>
          </div>

          {/* Secondary nav (desktop) */}
          <nav
            className="fleet-secondary"
            data-testid="secondary-nav"
            style={{
              height: TOPBAR_SECONDARY_H, alignItems: "center",
              padding: "0 6px", gap: 0,
              overflowX: "auto", scrollbarWidth: "none",
              borderBottom: "1px solid rgba(42,74,58,0.5)",
            }}
          >
            {secondaryItems.map(({ label, path }) => (
              <SecondaryTab
                key={path}
                label={label}
                path={path}
                active={navActive(path)}
                onClick={() => navigate(path)}
              />
            ))}
          </nav>
        </header>

        {/* ── Mobile drawer overlay ── */}
        {mobileOpen && (
          <div
            style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.6)" }}
            onClick={() => setMobileOpen(false)}
          >
            <div
              style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: 280, background: "var(--brand-bg-sunken)", borderRight: `1px solid ${colors.borderDefault}`, display: "flex", flexDirection: "column", overflowY: "auto" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px 16px", borderBottom: `1px solid ${colors.borderDefault}` }}>
                <img src={brand.logo_url} alt={brand.name} style={{ width: 24, height: 24 }} />
                <span style={{ fontWeight: 700, fontSize: fontSizes.sm, color: brand.accent_color }}>{brand.name}</span>
                <button onClick={() => setMobileOpen(false)} aria-label="Close navigation" style={{ marginLeft: "auto", background: "none", border: "none", color: colors.textMuted, cursor: "pointer", fontSize: "1.1rem" }}>✕</button>
              </div>

              <div style={{ padding: "12px 0" }}>
                <div style={{ fontSize: "0.65rem", fontWeight: 700, color: colors.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", padding: "0 16px 6px" }}>Fleet</div>
                {fleetItems.map(({ label, path }) => (
                  <button key={path} onClick={() => navigate(path)} style={drawerRowStyle(navActive(path))}>{label}</button>
                ))}
              </div>
              <div style={{ height: 1, background: colors.borderDefault, margin: "0 16px" }} />
              <div style={{ padding: "12px 0" }}>
                <div style={{ fontSize: "0.65rem", fontWeight: 700, color: colors.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", padding: "0 16px 6px" }}>Account</div>
                {accountItems.map(({ label, path }) => (
                  <button key={path} onClick={() => navigate(path)} style={drawerRowStyle(navActive(path))}>{label}</button>
                ))}
              </div>
              <div style={{ height: 1, background: colors.borderDefault, margin: "0 16px" }} />
              <div style={{ padding: "12px 16px" }}>
                {email && <div style={{ fontSize: fontSizes.xs, color: colors.textMuted, marginBottom: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email}</div>}
                <button onClick={doSignOut} style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: "none", color: colors.error, padding: "6px 0", fontSize: fontSizes.sm, cursor: "pointer", fontFamily: "inherit" }}>Sign out</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Main content ── */}
        <main style={{ flex: 1, overflowY: "auto" }}>
          <TrialBanner tierExpiresAt={profile?.tier_expires_at} status={profile?.status} />
          {profile?.status === "tier_expired" && !isTierExpiredAllowed(location.pathname)
            ? <TierExpiredBlock />
            : children
          }
        </main>

        <footer style={{ flexShrink: 0, padding: "0.75rem 1.5rem", display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", fontSize: "0.7rem", color: "var(--brand-text-secondary)", opacity: 0.85, borderTop: "1px solid var(--brand-border-subtle)" }}>
          <span>© {brand.name}</span>
          <a href="/privacy" style={{ color: "inherit", textDecoration: "none" }}>Privacy</a>
          <a href="/terms"   style={{ color: "inherit", textDecoration: "none" }}>Terms</a>
          <a href="/cookies" style={{ color: "inherit", textDecoration: "none" }}>Cookies</a>
        </footer>

        {authJob && <AuthRequiredModal job={authJob} onDismiss={() => setAuthJob(null)} />}

        {/* #2323 — Cmd+K command palette */}
        {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} />}

        {/* #2324 — keyboard shortcuts cheat sheet */}
        {shortcutsOpen && <KeyboardShortcutsSheet onClose={() => setShortcutsOpen(false)} />}

        {/* #2325 — hydrate theme from server profile (no-op render) */}
        <ThemeHydrator serverTheme={profile?.theme_preference} />
      </div>
    </>
  );
}
